// Library Methods
import { useTranslation } from "react-i18next";
import { useMemo } from "react";

// Components
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import SelectExamCard from "./SelectExamCard/SelectExamCard";
import { ChevronDownIcon } from "src/assets/icons";

// Styles
import { getSurfaceTokens } from "src/styles/mui/muiStylesHelper";

// Contexts
import { useExternalExamsContext } from "src/contexts/exams/ExternalExamsContext";

// Collapsed shows just the header bar, preview a single scrolling row of exams, and
// fullscreen a grid of every exam in place of the comparison grid.
export type SelectExamsFooterStateType = "collapsed" | "preview" | "fullscreen";

// The order the header's button steps through, wrapping back to the start
const NEXT_FOOTER_STATE: Record<
  SelectExamsFooterStateType,
  SelectExamsFooterStateType
> = {
  collapsed: "preview",
  preview: "fullscreen",
  fullscreen: "collapsed",
};

type SelectExamsFooterType = {
  selectedExamIds: Set<string>;
  onToggleExamSelection: (examId: string) => void;
  footerState: SelectExamsFooterStateType;
  onFooterStateChange: (footerState: SelectExamsFooterStateType) => void;
};

const SelectExamsFooter = ({
  selectedExamIds,
  onToggleExamSelection,
  footerState,
  onFooterStateChange,
}: SelectExamsFooterType) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { onSurface, dividerBorder } = getSurfaceTokens(theme);
  const { sortableCards } = useExternalExamsContext();

  const isExpanded = footerState === "fullscreen";

  // Only exams not already in the comparison, newest first -- this list is
  // always ordered by date, never manually reorderable.
  const availableExams = useMemo(
    () =>
      sortableCards
        .filter((exam) => !selectedExamIds.has(exam.id))
        .sort(
          (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
        ),
    [sortableCards, selectedExamIds]
  );

  return (
    <Box
      sx={{
        borderTop: dividerBorder,
        backgroundColor:
          theme.palette.mode === "dark" ? "#44485E" : "#5B6D7A30",
        display: isExpanded ? "flex" : "block",
        flexDirection: "column",
        flex: isExpanded ? 1 : "0 0 auto",
        minHeight: 0,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "8px",
          height: "40px",
          minHeight: "40px",
          px: "16px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            flex: 1,
            minWidth: 0,
            height: "100%",
            cursor: "pointer",
          }}
        >
          <Typography fontSize="14px" fontWeight={600}>
            {t("Select Exams")}
          </Typography>
        </Box>

        <IconButton
          disableRipple
          disableFocusRipple
          onClick={() => onFooterStateChange(NEXT_FOOTER_STATE[footerState])}
          aria-label={
            isExpanded ? t("Collapse exam list") : t("Expand exam list")
          }
          sx={{ color: onSurface }}
        >
          {isExpanded ? (
            <ChevronDownIcon width="16px" height="16px" />
          ) : (
            <ChevronDownIcon
              width="16px"
              height="16px"
              style={{
                transform: "rotate(180deg)",
              }}
            />
          )}
        </IconButton>
      </Box>

      {isExpanded ? (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(110px, 1fr))",
            alignItems: "start",
            alignContent: "start",
            gap: "8px",
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            px: "16px",
            pb: "16px",
          }}
        >
          {availableExams.map((exam) => (
            <SelectExamCard
              key={exam.id}
              selectedExam={exam}
              onToggleSelect={() => onToggleExamSelection(exam.id)}
            />
          ))}
        </Box>
      ) : (
        footerState === "preview" && (
          <Box
            sx={{
              display: "flex",
              gap: "8px",
              overflowX: "auto",
              px: "16px",
              pb: "16px",
            }}
          >
            {availableExams.map((exam) => (
              <Box key={exam.id} sx={{ width: "120px", flexShrink: 0 }}>
                <SelectExamCard
                  selectedExam={exam}
                  onToggleSelect={() => onToggleExamSelection(exam.id)}
                />
              </Box>
            ))}
          </Box>
        )
      )}
    </Box>
  );
};

export default SelectExamsFooter;
